import { useEffect, useRef } from 'react';

/**
 * Robust WebSocket hook — handles React StrictMode double-mount by deferring connect.
 * Avoids "close-before-connect" warnings and provides auto-reconnect.
 *
 * @param {string|null} url - full WS url (wss://.../api/ws/...). Pass null to disable.
 * @param {object} options
 * @param {function} options.onMessage - (parsedData) => void
 * @param {function} options.onOpen - (ws) => void  (use to send auth message)
 * @param {function} options.onStatusChange - (connected:boolean) => void
 * @param {number} options.reconnectMs - default 5000
 */
export function useWebSocket(url, { onMessage, onOpen, onStatusChange, reconnectMs = 5000 } = {}) {
  // Stash callbacks in refs so the connect closure always sees latest without re-binding
  const onMessageRef = useRef(onMessage);
  const onOpenRef = useRef(onOpen);
  const onStatusRef = useRef(onStatusChange);
  onMessageRef.current = onMessage;
  onOpenRef.current = onOpen;
  onStatusRef.current = onStatusChange;

  useEffect(() => {
    if (!url) return undefined;

    let ws = null;
    let reconnectTimer = null;
    let openTimer = null;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      try {
        ws = new WebSocket(url);

        ws.onopen = () => {
          if (cancelled) {
            try { ws.close(1000); } catch { /* noop */ }
            return;
          }
          onStatusRef.current?.(true);
          onOpenRef.current?.(ws);
        };

        ws.onmessage = (e) => {
          if (cancelled) return;
          try {
            const data = JSON.parse(e.data);
            onMessageRef.current?.(data, ws);
          } catch { /* noop */ }
        };

        ws.onclose = () => {
          onStatusRef.current?.(false);
          if (!cancelled) {
            reconnectTimer = setTimeout(connect, reconnectMs);
          }
        };

        ws.onerror = () => {
          // close() will fire onclose which schedules reconnect
          try { ws.close(); } catch { /* noop */ }
        };
      } catch {
        if (!cancelled) reconnectTimer = setTimeout(connect, reconnectMs);
      }
    };

    // Defer initial connect by one tick — StrictMode unmount fires before this resolves,
    // so the synthetic double-mount won't open a real WS that then needs to be closed.
    openTimer = setTimeout(connect, 80);

    return () => {
      cancelled = true;
      clearTimeout(openTimer);
      clearTimeout(reconnectTimer);
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        try { ws.close(1000); } catch { /* noop */ }
      }
      ws = null;
    };
    // url alone determines lifecycle; callbacks are read via refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, reconnectMs]);
}

/** Build the WS url for a backend path. Returns null if backend env not set. */
export function buildWsUrl(path) {
  const base = process.env.REACT_APP_BACKEND_URL || '';
  if (!base) return null;
  return base.replace(/^http/, 'ws') + path;
}
